/**
 * RegulatoryChangeView (P158) -- the regulatory-change autopilot.
 *
 * Paste a regulatory change; the Engine identifies which existing
 * obligations it affects (grounded -- invented ids dropped + shown) +
 * their compliance exposure (controls / open gaps).
 */
import { formatDate } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { hasPosition, useMe } from "shared/api/useMe";
import { api } from "shared/api/client";
import type {
  AffectedObligation,
  ChangeAssessment,
  ChangeTold,
} from "shared/api/types";
import { toldSentence } from "shared/changes/toldSentence";
import { EngineBusy } from "shared/components/EngineBusy";
import { EngineOffBanner } from "shared/components/EngineOffBanner";
import { GroundingNote } from "shared/components/GroundingNote";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";
import { refusalMessage } from "shared/api/refusalMessage";
import { useToast } from "shared/toast/ToastProvider";
import {
  useAssessRegulatoryChange,
  useFindCandidateObligations,
  useChangeAssessments,
  useDraftRemediationCAP,
  useHandToPreparers,
} from "framework/api/useRegulatoryChange";
import { useSeatHolders } from "shared/api/useSeatHolders";

const IMPACT_PILL: Record<string, string> = {
  new_requirement: "bg-rag-redBg text-rag-red",
  tightened: "bg-rag-amberBg text-rag-amber",
  clarified: "bg-slate-100 text-slate-600",
};

function AffectedRow({ a }: { a: AffectedObligation }) {
  const { t } = useTranslation();
  return (
    <li
      data-testid={`reg-change-affected-${a.natural_id}`}
      className="rounded border border-slate-200 bg-white p-3"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className={`rounded px-2 py-0.5 text-[11px] font-medium ${
            IMPACT_PILL[a.impact_type] ?? ""
          }`}
        >
          {t(`modules.framework.regChange.impact.${a.impact_type}`)}
        </span>
        <span className="font-mono text-[11px] text-slate-500">
          {/* Round 18 A36 -- "sama_itgf sama_itgf:3.1.4": the id
              already carries its framework. */}
          {a.natural_id.startsWith(`${a.source_framework}:`)
            ? a.natural_id
            : `${a.source_framework} ${a.natural_id}`}
        </span>
        <span className="text-sm text-slate-900">{a.title}</span>
        <span className="ms-auto text-[11px] tabular-nums text-slate-500">
          {t("modules.framework.regChange.controls", { count: a.control_count })}
          {a.gap_count > 0 && (
            <span className="ms-1 rounded bg-rag-redBg px-1 text-rag-red">
              {t("modules.framework.regChange.gaps", { count: a.gap_count })}
            </span>
          )}
        </span>
      </div>
      {a.rationale && <p className="mt-1 text-xs text-slate-500">{a.rationale}</p>}
    </li>
  );
}

/** A CAP title derived from the circular's first non-empty line. */
function deriveCapTitle(changeText: string, fallback: string): string {
  const firstLine = changeText
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  const base = firstLine ?? fallback;
  return `Remediate: ${base}`.slice(0, 200);
}

const CAP_STATE_PILL: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600",
  in_review: "bg-rag-amberBg text-rag-amber",
  approved: "bg-rag-greenBg text-rag-green",
  sent_back: "bg-rag-redBg text-rag-red",
};

function LineagePanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const lineage = useChangeAssessments();
  // P421.2 -- the change register's own seats.
  const mayRaise =
    me.data !== undefined &&
    (me.data.is_admin ||
      hasPosition(me.data, "preparer") ||
      hasPosition(me.data, "approver") ||
      (me.data.realm_roles ?? []).includes("head_of_opsres"));
  const [raising, setRaising] = useState<string | null>(null);
  const raiseAsChange = async (assessmentId: string) => {
    setRaising(assessmentId);
    try {
      const row = await api.post<{
        id: string;
        raised_at?: string;
        already_raised?: boolean;
        told?: ChangeTold | null;
      }>(`/v1/twin/changes/from-regulatory-change/${assessmentId}`, {});
      /* Round 19 G39 -- the second press opened the change the first
         one raised, and said "Raised as a change." */
      toast.push({
        tone: "success",
        message:
          row.already_raised && row.raised_at
            ? t("modules.twin.changes.fromRegulatoryExisting", {
                date: formatDate(row.raised_at, i18n.language),
              })
            : [t("modules.twin.changes.fromRegulatoryDone"), toldSentence(t, row.told)]
                .filter(Boolean)
                .join(" "),
      });
      navigate(`/twin/changes?change=${row.id}`);
    } catch (err) {
      toast.push({ tone: "warning", message: refusalMessage(t, err) });
    } finally {
      setRaising(null);
    }
  };
  /* Round 19 N13 -- a preparer handed an assessment drafts from its
     row; the hand-off links here with ?draft=. */
  const [params] = useSearchParams();
  const named = params.get("draft");
  const canDraft = hasPosition(me.data, "preparer");
  const draft = useDraftRemediationCAP();
  const draftFrom = (a: ChangeAssessment) =>
    draft.mutate(
      {
        title: deriveCapTitle(
          a.change_text,
          t("modules.framework.regChange.draftCap.fallback"),
        ),
        change_text: a.change_text,
        obligation_ids: a.obligation_ids ?? [],
        assessment_id: a.id,
      },
      {
        onSuccess: (res) => {
          toast.push({
            tone: "success",
            message: t("modules.framework.regChange.draftCap.success", {
              count: res.linked_controls,
            }),
          });
          navigate(`/framework/caps/${res.cap_id}`);
        },
        onError: (err) =>
          toast.push({ tone: "warning", message: refusalMessage(t, err) }),
      },
    );
  const rows: ChangeAssessment[] = lineage.data ?? [];
  if (rows.length === 0) return null;
  return (
    <section data-testid="reg-change-lineage" className="mt-2">
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
        {t("modules.framework.regChange.lineage.heading")}
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {rows.map((a) => (
          <li
            key={a.id}
            data-testid={`reg-change-lineage-${a.id}`}
            data-named={named === a.id ? "true" : undefined}
            className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded border bg-white p-2.5 text-sm ${
              named === a.id
                ? "border-brand-brass ring-1 ring-brand-brass/40"
                : "border-slate-200"
            }`}
          >
            {a.cap_id && a.cap_state ? (
              <>
                <Link
                  to={`/framework/caps/${a.cap_id}`}
                  className="font-medium text-brand-brassDark hover:underline"
                >
                  {a.cap_title}
                </Link>
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    CAP_STATE_PILL[a.cap_state] ?? "bg-slate-100 text-slate-600"
                  }`}
                >
                  {t(`caps.detail.state_labels.${a.cap_state}`)}
                </span>
              </>
            ) : (
              /* Round 19 A42 -- assessed, and no plan drafted from it. */
              <>
                <span className="font-medium text-slate-800" dir="auto">
                  {a.change_text.length > 80
                    ? `${a.change_text.slice(0, 80)}…`
                    : a.change_text}
                </span>
                <span
                  data-testid={`reg-change-lineage-noplan-${a.id}`}
                  className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600"
                >
                  {t("modules.framework.regChange.lineage.noPlan")}
                </span>
                {named === a.id && (
                  <span className="text-[11px] text-brand-brassDark">
                    {t("modules.framework.regChange.lineage.handedToYou")}
                  </span>
                )}
                {canDraft && (a.obligation_ids ?? []).length > 0 && (
                  <button
                    type="button"
                    data-testid={`reg-change-lineage-draft-${a.id}`}
                    disabled={draft.isPending}
                    onClick={() => draftFrom(a)}
                    className="rounded bg-brand-brass px-2 py-0.5 text-[11px] font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
                  >
                    {t("modules.framework.regChange.lineage.draftHere")}
                  </button>
                )}
              </>
            )}
            <span className="text-[11px] text-slate-500">
              {t("modules.framework.regChange.lineage.obligations", {
                count: a.obligation_count,
              })}
            </span>
            <span className="ms-auto text-[11px] text-slate-400">
              {a.created_by_username ?? "--"} ·{" "}
              {formatDate(a.created_at, i18n.language)}
            </span>
            {mayRaise && (
              <button
                type="button"
                data-testid={`reg-change-raise-${a.id}`}
                disabled={raising === a.id}
                onClick={() => void raiseAsChange(a.id)}
                className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-[11px] text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
              >
                {t("modules.twin.changes.fromRegulatory")}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Round 19 N13 -- names who may draft, in the reader's language: up
 *  to three names, or two and how many others. */
function namesOf(
  names: string[],
  language: string,
  others: (n: number) => string,
): string {
  const shown =
    names.length > 3 ? [...names.slice(0, 2), others(names.length - 2)] : names;
  try {
    return new Intl.ListFormat(language, { type: "disjunction" }).format(shown);
  } catch {
    return shown.join(", ");
  }
}

/** Round 19 N13 -- an approver who assessed the circular was told
 *  "Drafting a CAP requires the preparer position": the seat, not who
 *  holds it, and no way to hand the draft on. */
function DraftIsThePreparers({
  assessmentId,
}: {
  assessmentId: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const seats = useSeatHolders();
  const hand = useHandToPreparers();
  const names = [...new Set(seats.data?.holders?.preparer ?? [])];
  const sentence = !seats.data?.holders
    ? t("modules.framework.regChange.draftCap.locked")
    : names.length === 0
      ? t("modules.framework.regChange.draftCap.lockedNobody")
      : t("modules.framework.regChange.draftCap.lockedNamed", {
          names: namesOf(names, i18n.language, (n) =>
            t("modules.framework.regChange.draftCap.others", { count: n }),
          ),
        });
  return (
    <div data-testid="reg-change-act-locked" className="flex flex-col gap-1.5">
      <p className="text-xs text-slate-600">{sentence}</p>
      {assessmentId && (
        <button
          type="button"
          data-testid="reg-change-hand-to-preparers"
          disabled={hand.isPending || hand.isSuccess}
          onClick={() =>
            hand.mutate(assessmentId, {
              onSuccess: (res) =>
                toast.push({
                  tone: "success",
                  message:
                    res.told === 0
                      ? t("modules.framework.regChange.draftCap.sentNobodyYet")
                      : t("modules.framework.regChange.draftCap.sent", {
                          count: res.told,
                        }),
                }),
              onError: (err) =>
                toast.push({ tone: "warning", message: refusalMessage(t, err) }),
            })
          }
          className="self-start rounded border border-brand-brass/40 bg-brand-brass/5 px-2.5 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {hand.isPending
            ? t("modules.framework.regChange.draftCap.sending")
            : t("modules.framework.regChange.draftCap.sendToPreparers")}
        </button>
      )}
    </div>
  );
}

export function RegulatoryChangeView(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const [text, setText] = useState("");
  const assess = useAssessRegulatoryChange();
  const candidates = useFindCandidateObligations();
  const [picked, setPicked] = useState<string[]>([]);
  const draft = useDraftRemediationCAP();
  const data = assess.data;
  const canDraft = hasPosition(me.data, "preparer");

  function onDraftCap() {
    if (!data || data.affected.length === 0) return;
    draft.mutate(
      {
        title: deriveCapTitle(text, t("modules.framework.regChange.draftCap.fallback")),
        change_text: text.trim(),
        obligation_ids: data.affected.map((a) => a.obligation_id),
        ...(data.assessment_id ? { assessment_id: data.assessment_id } : {}),
      },
      {
        onSuccess: (res) => {
          toast.push({
            tone: "success",
            message: t("modules.framework.regChange.draftCap.success", {
              count: res.linked_controls,
            }),
            ...(res.compliant_not_linked
              ? {
                  detail: t("modules.framework.regChange.draftCap.compliantLeftOff", {
                    count: res.compliant_not_linked,
                  }),
                }
              : {}),
          });
          navigate(`/framework/caps/${res.cap_id}`);
        },
        onError: (e) =>
          toast.push({
            tone: "error",
            message: e.detail ?? t("modules.framework.regChange.draftCap.error"),
          }),
      },
    );
  }

  return (
    <section data-testid="reg-change-view" className="flex flex-col gap-4">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t("modules.framework.regChange.rubric")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="obligation"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("modules.framework.regChange.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("modules.framework.regChange.intro")}
        </p>
      </header>

      <EngineOffBanner manualKey="engineOff.regChange" />

      <textarea
        dir="auto"
        data-testid="reg-change-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("modules.framework.regChange.placeholder")}
        rows={6}
        className="w-full rounded-md border border-slate-300 p-3 text-sm focus:border-brand-brass focus:outline-none"
      />
      {/* Round 16 H27 -- the two buttons read "Assess impactFind
          candidate obligations" as text; a flex gap spaces them on
          screen and a space separates them in the text. */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="reg-change-assess"
          onClick={() => text.trim() && assess.mutate(text.trim())}
          disabled={!text.trim() || assess.isPending}
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {assess.isPending ? (
            <EngineBusy label={t("modules.framework.regChange.assessing")} />
          ) : (
            t("modules.framework.regChange.assess")
          )}
        </button>{" "}
        {/* C9 -- the manual path. Retrieval is pg_trgm and needs no
            Engine, so with it off an operator can still be shown the
            obligations closest to the circular and pick the ones that
            apply. The screen used to offer a text box, a button and a
            503. */}
        <button
          type="button"
          data-testid="reg-change-find-candidates"
          onClick={() => text.trim() && candidates.mutate(text.trim())}
          disabled={!text.trim() || candidates.isPending}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {candidates.isPending
            ? t("modules.framework.regChange.finding")
            : t("modules.framework.regChange.findCandidates")}
        </button>
      </div>

      {candidates.data && (
        <section data-testid="reg-change-candidates" className="flex flex-col gap-2">
          <p className="text-sm text-slate-600">
            {t("modules.framework.regChange.candidatesIntro", {
              count: candidates.data.candidates_considered,
            })}
          </p>
          <ul className="flex flex-col gap-1">
            {candidates.data.candidates.map((c) => (
              <li
                key={c.obligation_id}
                data-testid="reg-change-candidate"
                className="flex items-start gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-sm"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  data-testid={`reg-change-pick-${c.natural_id}`}
                  checked={picked.includes(c.obligation_id)}
                  onChange={(e) =>
                    setPicked((prev) =>
                      e.target.checked
                        ? [...prev, c.obligation_id]
                        : prev.filter((x) => x !== c.obligation_id),
                    )
                  }
                />
                <span className="min-w-0">
                  <span className="font-mono text-[11px] text-slate-400">
                    {c.natural_id}
                  </span>{" "}
                  <span className="text-slate-900">{c.title}</span>
                  <span className="block text-xs text-slate-500">
                    {t("modules.framework.regChange.candidateCounts", {
                      controls: c.control_count,
                      gaps: c.gap_count,
                    })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {canDraft && (
            <div>
              <button
                type="button"
                data-testid="reg-change-log-manual"
                disabled={picked.length === 0 || draft.isPending}
                onClick={() =>
                  draft.mutate(
                    {
                      obligation_ids: picked,
                      change_text: text.trim(),
                      title: text.trim().split("\n")[0]?.slice(0, 120) ?? "Circular",
                    },
                    {
                      onSuccess: (r) => {
                        toast.push({
                          tone: "success",
                          message: t("modules.framework.regChange.draftCap.success", {
                            count: r.linked_controls,
                          }),
                        });
                        navigate(`/framework/caps/${r.cap_id}`);
                      },
                      onError: (e) =>
                        toast.push({ tone: "error", message: e.detail ?? "error" }),
                    },
                  )
                }
                className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1.5 text-sm font-semibold text-brand-navy disabled:opacity-50"
              >
                {t("modules.framework.regChange.logManual", {
                  count: picked.length,
                })}
              </button>
            </div>
          )}
        </section>
      )}

      {assess.isError && (
        <p data-testid="reg-change-error" className="text-sm text-rag-red">
          {/* Round 16 A22 -- the Engine's refusal is a sentence the
              product already says; "Could not assess the change." hid it. */}
          {assess.error.status === 503
            ? t("modules.framework.regChange.engineOff")
            : assess.error.code
              ? refusalMessage(t, assess.error)
              : t("modules.framework.regChange.error")}
        </p>
      )}

      {data && (
        <section data-testid="reg-change-result" className="flex flex-col gap-2">
          <div
            data-testid="reg-change-headline"
            className={`rounded-md px-3 py-2 text-sm font-semibold ${
              data.obligations_affected > 0
                ? "bg-rag-amberBg text-rag-amber"
                : "bg-rag-greenBg text-rag-green"
            }`}
          >
            {data.obligations_affected > 0
              ? t("modules.framework.regChange.headline", {
                  affected: data.obligations_affected,
                  gaps: data.obligations_with_gaps,
                })
              : t("modules.framework.regChange.none")}
          </div>
          {/* Round 19 A45 -- how wide the shortlist the Engine judged was. */}
          {data.candidates_considered > 0 && (
            <p data-testid="reg-change-shortlist" className="text-xs text-slate-600">
              {t("modules.framework.regChange.chosenFrom", {
                count: data.candidates_considered,
              })}
            </p>
          )}
          {data.affected.length > 0 && (
            <ul className="flex flex-col gap-2">
              {data.affected.map((a) => (
                <AffectedRow key={a.obligation_id} a={a} />
              ))}
            </ul>
          )}

          {data.affected.length > 0 &&
            (canDraft ? (
              <div data-testid="reg-change-act">
                <button
                  type="button"
                  data-testid="reg-change-draft-cap"
                  onClick={onDraftCap}
                  disabled={draft.isPending}
                  className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
                >
                  {draft.isPending
                    ? t("modules.framework.regChange.draftCap.drafting")
                    : t("modules.framework.regChange.draftCap.button")}
                </button>
                <p className="mt-1 text-xs text-slate-500">
                  {t("modules.framework.regChange.draftCap.hint")}
                </p>
              </div>
            ) : (
              <DraftIsThePreparers assessmentId={data.assessment_id ?? null} />
            ))}

          <GroundingNote grounding={data.grounding} />
          <EngineLanguageNote />
        </section>
      )}

      <LineagePanel />
    </section>
  );
}
